import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAutenticacao } from '../lib/auth';
import { api } from '../lib/api';
import InstalarApp from './InstalarApp';

type Item = {
  rotulo: string;
  para: string;
  icone: string;
  papeis: string[];
  emBreve?: boolean;
  /** aparece tambem para quem lidera uma equipe, seja qual for o papel */
  paraLider?: boolean;
  /** aparece tambem para quem serve na midia, seja qual for o papel */
  paraQuadro?: boolean;
  /** como o item se chama para os 12 da igreja (papeis lider e lider_adm) */
  rotuloDoze?: string;
  /** nome curto para a barra de baixo do celular */
  curto?: string;
  /** escondido do menu durante a repaginada (a pagina continua existindo) */
  oculto?: boolean;
};

const ITENS: Item[] = [
  { rotulo: 'Meu dia', para: '/painel', icone: 'relogio', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm', 'voluntario', 'membro'] },
  // quadro: administracao, midia e os 12 da igreja (lider e lider_adm), com edicao
  { rotulo: 'Quadros', para: '/quadros', icone: 'quadro', papeis: ['admin', 'editor', 'lider', 'lider_adm'], paraQuadro: true },
  { rotulo: 'Calendário', curto: 'Agenda', para: '/agenda', icone: 'calendario', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm', 'voluntario', 'membro'] },
  // voluntario ve a escala geral do ministerio dele; membro comum nao ve escala
  { rotulo: 'Escalas', para: '/escalas', icone: 'pessoas', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm', 'voluntario'], oculto: true },
  { rotulo: 'Minhas escalas', curto: 'Minhas', para: '/minhas-escalas', icone: 'lista', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm', 'voluntario'] },
  // escala de lideres: so o admin monta; lideres (de equipe ou de sub, seja qual
  // for o papel da conta) veem tudo e confirmam a propria presenca. Para os 12
  // ela se chama "Escala 12 da Igreja" — e assim que a igreja fala deles.
  // Fica depois de Minhas escalas para nao tirar ninguem da barra do celular,
  // que mostra so os cinco primeiros itens.
  { rotulo: 'Escala de líderes', rotuloDoze: 'Escala 12 da Igreja', para: '/escala-lideres', icone: 'estrela', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm'], paraLider: true, oculto: true },
  // escala dos cultos (Culto da Fe, Arena e Domingo): o admin escolhe a equipe de
  // cada demanda; o 12 e o ADM delas entram sozinhos. Os 12 e ADMs veem tudo.
  { rotulo: 'Escala dos cultos', curto: 'Cultos', para: '/escala-cultos', icone: 'calendario', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm'], paraLider: true },
  { rotulo: 'Palavras', para: '/palavras', icone: 'play', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm', 'voluntario', 'membro'], oculto: true },
  { rotulo: 'Fotos', para: '/fotos', icone: 'galeria', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm', 'voluntario', 'membro'], oculto: true },
  // area de lideres: lideranca ve todas as equipes; o 12 e o ADM veem so a deles,
  // por isso para eles o item fala no singular
  { rotulo: 'Equipes', rotuloDoze: 'Equipe', para: '/equipes', icone: 'pessoas', papeis: ['admin', 'editor', 'viewer', 'lider', 'lider_adm'], paraLider: true },
  // placar das equipes: so o pastor e a administracao
  { rotulo: 'Placar', para: '/placar', icone: 'lista', papeis: ['admin', 'viewer'], oculto: true },
  { rotulo: 'Visitantes', para: '/visitantes', icone: 'mao', papeis: ['admin', 'editor'], oculto: true },
  { rotulo: 'Aprovações', para: '/aprovacoes', icone: 'aprovar', papeis: ['admin', 'editor'], oculto: true },
  // dinheiro da igreja: administracao e lideranca. Equipe de midia nao entra.
  { rotulo: 'Tesouraria', para: '/financeiro', icone: 'lista', papeis: ['admin', 'viewer'], oculto: true },
  { rotulo: 'Equipe', para: '/equipe', icone: 'engrenagem', papeis: ['admin'] },
  { rotulo: 'Logs', para: '/logs', icone: 'lista', papeis: ['admin'], oculto: true },
];

const NOME_DO_PAPEL: Record<string, string> = {
  admin: 'Admin total',
  editor: 'Equipe de mídia',
  viewer: 'Liderança · leitura',
  // os 12 cuidam das equipes — por isso o acesso deles nao se chama "lider"
  lider: '12 da Igreja',
  lider_adm: '12 da Igreja · ADM',
  voluntario: 'Voluntário',
  membro: 'Membro',
};

const CHAVE_MENU = 'sara:menu-minimizado';

export default function Layout({
  children,
  titulo,
  subtitulo,
  acoes,
}: {
  children: React.ReactNode;
  titulo?: string;
  subtitulo?: string;
  acoes?: React.ReactNode;
}) {
  const { usuario, sair } = useAutenticacao();
  const local = useLocation();
  const papel = usuario?.papel ?? 'membro';
  const itens = ITENS.filter(
    (i) =>
      !i.oculto &&
      (i.papeis.includes(papel) ||
      (i.paraLider && usuario?.lideraEquipe) ||
      (i.paraQuadro && usuario?.podeQuadro)),
  );
  // para os 12 da igreja alguns itens mudam de nome (ex.: "Escala 12 da Igreja")
  const doze = papel === 'lider' || papel === 'lider_adm';
  const rotuloDe = (item: Item) => (doze && item.rotuloDoze ? item.rotuloDoze : item.rotulo);
  const navegaveis = itens.filter((i) => !i.emBreve);
  const sobraDaBarra = navegaveis.length > 5;
  const naBarra = navegaveis.slice(0, 5);
  const itensDaGaveta = navegaveis.slice(5);

  // o jeito que a pessoa deixou o menu fica lembrado no aparelho dela
  const [minimizado, setMinimizado] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_MENU) === '1';
    } catch {
      return false;
    }
  });
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const [pendentes, setPendentes] = useState(0);

  // contador de pedidos esperando resposta, para admin e editor
  useEffect(() => {
    if (papel !== 'admin' && papel !== 'editor') return;
    let vivo = true;
    const buscar = () =>
      api<{ pendentes: number }>('/solicitacoes?status=pendente')
        .then((d) => vivo && setPendentes(d.pendentes))
        .catch(() => undefined);
    void buscar();
    const t = setInterval(buscar, 120000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [papel]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_MENU, minimizado ? '1' : '0');
    } catch {
      /* navegador sem armazenamento: segue sem lembrar */
    }
  }, [minimizado]);

  // trocar de tela fecha a gaveta do celular
  useEffect(() => setGavetaAberta(false), [local.pathname]);

  const menu = (aberto: boolean, aoNavegar?: () => void) => (
    <div className="flex h-full flex-col">
      <div className={`flex items-center gap-3 px-4 pt-5 ${aberto ? '' : 'justify-center px-0'}`}>
        {/* o logo sempre leva para o inicio */}
        <Link to="/painel" onClick={aoNavegar} aria-label="Ir para o inicio" className="shrink-0">
          <img src="/logo.png" alt="Sara Osasco" className="h-9 w-9 object-contain" />
        </Link>
        {aberto && (
          <Link to="/painel" onClick={aoNavegar} className="leading-tight">
            <span className="block font-bold text-white">Sara</span>
            <span className="block text-[11px] uppercase tracking-[0.25em] text-white/70">Osasco</span>
          </Link>
        )}
      </div>

      {aberto && (
        <p className="px-5 pb-1 pt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/30">
          Menu
        </p>
      )}

      <nav className={`flex flex-1 flex-col gap-1 ${aberto ? 'px-3 pt-1' : 'items-center px-0 pt-8'}`}>
        {itens.map((item) => {
          const ativo = local.pathname === item.para;
          const classe = aberto
            ? `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                ativo
                  ? 'bg-white font-semibold text-tinta-900'
                  : item.emBreve
                    ? 'text-white/25'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`
            : `group relative flex h-11 w-11 items-center justify-center rounded-xl transition ${
                ativo
                  ? 'bg-white text-tinta-900'
                  : item.emBreve
                    ? 'text-white/25'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
              }`;

          const conteudo = (
            <>
              <span className="relative">
                <Icone nome={item.icone} />
                {item.para === '/aprovacoes' && pendentes > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold text-tinta-900">
                    {pendentes}
                  </span>
                )}
              </span>
              {aberto ? (
                <>
                  <span className="truncate">{rotuloDe(item)}</span>
                  {item.emBreve && (
                    <span className="ml-auto rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase">
                      em breve
                    </span>
                  )}
                </>
              ) : (
                <span className="pointer-events-none absolute left-14 z-30 hidden whitespace-nowrap rounded-lg bg-tinta-900 px-2.5 py-1 text-xs text-white shadow-barra group-hover:block">
                  {rotuloDe(item)}
                  {item.emBreve ? ' · em breve' : ''}
                </span>
              )}
            </>
          );

          return item.emBreve ? (
            <span key={item.para} className={classe}>
              {conteudo}
            </span>
          ) : (
            <Link key={item.para} to={item.para} onClick={aoNavegar} className={classe} aria-label={rotuloDe(item)}>
              {conteudo}
            </Link>
          );
        })}
      </nav>

      <div className={`border-t border-white/10 ${aberto ? 'p-4' : 'flex flex-col items-center py-4'}`}>
        {aberto ? (
          <>
            <p className="truncate text-sm font-semibold text-white">{usuario?.nome}</p>
            <p className="text-xs text-white/50">{NOME_DO_PAPEL[papel] ?? papel}</p>
            <div className="mt-3">
              <InstalarApp />
            </div>
            <button
              onClick={() => void sair()}
              className="mt-3 w-full rounded-xl border border-white/15 py-2 text-sm text-white/80 transition hover:bg-white/10"
            >
              Sair
            </button>
          </>
        ) : (
          <>
            <InstalarApp compacto />
            <button
              onClick={() => void sair()}
              aria-label="Sair"
              className="group relative flex h-11 w-11 items-center justify-center rounded-xl text-white/60 transition hover:bg-white/10 hover:text-white"
            >
              <Icone nome="sair" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-full bg-fundo">
      <div className="mx-auto flex max-w-[1400px] gap-4 p-3 md:p-4">
        <aside
          className={`sticky top-4 hidden h-[calc(100vh-2rem)] shrink-0 rounded-3xl bg-tinta-900 shadow-barra transition-[width] md:block ${
            minimizado ? 'w-[76px]' : 'w-60'
          }`}
        >
          {menu(!minimizado)}
        </aside>

        <div className="min-w-0 flex-1 pb-24 md:pb-0">
          <header className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3.5 shadow-cartao md:px-5">
            <div className="flex min-w-0 items-center gap-3">
              {/* celular: abre a gaveta. computador: minimiza a barra */}
              <button
                onClick={() => setGavetaAberta(true)}
                aria-label="Abrir menu"
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-tinta-200 text-tinta-700 md:hidden"
              >
                <Icone nome="menu" />
              </button>
              <button
                onClick={() => setMinimizado((m) => !m)}
                aria-label={minimizado ? 'Expandir menu' : 'Minimizar menu'}
                title={minimizado ? 'Expandir menu' : 'Minimizar menu'}
                className="hidden h-9 w-9 items-center justify-center rounded-xl border border-tinta-200 text-tinta-700 transition hover:bg-tinta-100 md:flex"
              >
                <Icone nome={minimizado ? 'expandir' : 'recolher'} />
              </button>

              <div className="min-w-0">
                {titulo && <h1 className="truncate text-lg font-bold text-tinta-900 md:text-2xl">{titulo}</h1>}
                {subtitulo && <p className="mt-0.5 truncate text-sm text-tinta-400">{subtitulo}</p>}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              {acoes}
              <span className="hidden items-center gap-2 border-l border-tinta-200 pl-3 sm:flex">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-tinta-900 text-sm font-semibold uppercase text-white">
                  {usuario?.nome?.trim().charAt(0) ?? '?'}
                </span>
                <span className="leading-tight">
                  <span className="block max-w-[10rem] truncate text-sm font-semibold text-tinta-900">
                    {usuario?.nome}
                  </span>
                  <span className="block text-xs text-tinta-400">{NOME_DO_PAPEL[papel] ?? papel}</span>
                </span>
              </span>
            </div>
          </header>

          {children}
        </div>
      </div>

      {/* gaveta do celular */}
      {gavetaAberta && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setGavetaAberta(false)}>
          <div className="absolute inset-0 bg-tinta-900/40" />
          <aside
            className="absolute inset-y-0 left-0 w-[17rem] max-w-[85vw] overflow-y-auto rounded-r-3xl bg-tinta-900 shadow-barra"
            onClick={(e) => e.stopPropagation()}
          >
            {menu(true, () => setGavetaAberta(false))}
          </aside>
        </div>
      )}

      {/* barra de baixo do celular: os 5 primeiros itens e, se sobrar, "Mais" abre a gaveta */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex justify-around gap-1 border-t border-tinta-200 bg-white/95 px-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1.5 backdrop-blur md:hidden">
        {naBarra.map((item) => {
          const ativo = local.pathname === item.para;
          return (
            <Link
              key={item.para}
              to={item.para}
              className={`flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-0.5 py-1.5 text-[10px] font-medium transition ${
                ativo ? 'bg-tinta-900 text-white' : 'text-tinta-400'
              }`}
            >
              <Icone nome={item.icone} />
              <span className="w-full truncate text-center">{item.curto && !(doze && item.rotuloDoze) ? item.curto : rotuloDe(item)}</span>
            </Link>
          );
        })}
        {sobraDaBarra && (
          <button
            onClick={() => setGavetaAberta(true)}
            className={`flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-0.5 py-1.5 text-[10px] font-medium transition ${
              itensDaGaveta.some((i) => i.para === local.pathname) ? 'bg-tinta-900 text-white' : 'text-tinta-400'
            }`}
          >
            <Icone nome="menu" />
            <span className="w-full truncate text-center">Mais</span>
          </button>
        )}
      </nav>
    </div>
  );
}

function Icone({ nome }: { nome: string }) {
  const p = {
    className: 'h-5 w-5 shrink-0',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (nome) {
    case 'relogio':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'quadro':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <rect x="3" y="4" width="6" height="16" rx="1.5" />
          <rect x="11" y="4" width="6" height="10" rx="1.5" />
          <rect x="19" y="4" width="2" height="14" rx="1" />
        </svg>
      );
    case 'calendario':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      );
    case 'pessoas':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3 20a6 6 0 0 1 12 0M16.5 11a3 3 0 1 0 0-6M18.5 20a6 6 0 0 0-3-5" />
        </svg>
      );
    case 'lista':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <path d="M8 6h12M8 12h12M8 18h8" />
          <circle cx="4" cy="6" r="1" />
          <circle cx="4" cy="12" r="1" />
          <circle cx="4" cy="18" r="1" />
        </svg>
      );
    case 'mao':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <path d="M8 13V5a1.5 1.5 0 0 1 3 0v6" />
          <path d="M11 11V4.5a1.5 1.5 0 0 1 3 0V11" />
          <path d="M14 11V6.5a1.5 1.5 0 0 1 3 0V13" />
          <path d="M17 12.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-1.5a6 6 0 0 1-5.3-3.2L5 14.5a1.6 1.6 0 0 1 2.6-1.8L9 14" />
        </svg>
      );
    case 'estrela':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" />
        </svg>
      );
    case 'aprovar':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <path d="M9 12l2 2 4-4" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
    case 'engrenagem':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9 2 2 0 1 1-2.8 2.8 1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2 2 2 0 1 1-2.8-2.8A1.7 1.7 0 0 0 4.6 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.2-2.9 2 2 0 1 1 2.8-2.8A1.7 1.7 0 0 0 12 4.6a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2 2 2 0 1 1 2.8 2.8 1.7 1.7 0 0 0 1.2 2.9 2 2 0 1 1 0 4 1.7 1.7 0 0 0-1.5 1.5z" />
        </svg>
      );
    case 'menu':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      );
    case 'recolher':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <rect x="3" y="4" width="18" height="16" rx="3" />
          <path d="M15 9l-3 3 3 3" />
          <path d="M9 4v16" />
        </svg>
      );
    case 'expandir':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <rect x="3" y="4" width="18" height="16" rx="3" />
          <path d="M12 9l3 3-3 3" />
          <path d="M9 4v16" />
        </svg>
      );
    case 'play':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <rect x="2.5" y="5" width="19" height="14" rx="3" />
          <path d="M10.5 9.5l4.5 2.5-4.5 2.5z" />
        </svg>
      );
    case 'galeria':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <rect x="3" y="5" width="18" height="14" rx="3" />
          <circle cx="8.5" cy="10" r="1.5" />
          <path d="M21 16l-5-5-6 6" />
        </svg>
      );
    case 'sair':
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
          <path d="M10 17l-5-5 5-5M5 12h12" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" {...p}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}
